import React, {
  CSSProperties,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { SemanticICONS } from "semantic-ui-react";

import moment from "moment";

// UX
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";

type TimeComboBoxProps = {
  id?: string;
  style?: CSSProperties;
  inputStyle?: CSSProperties;
  // data
  defaultValue?: string;
  icon?: SemanticICONS;
  interval?: number;
  maxHour?: number;
  minHour?: number;
  placeholder?: string;
  // config
  disabled?: boolean;
  disableReorder?: boolean; // ไม่เรียงใหม่ + แทรก missing value ตามลำดับ
  fluid?: boolean;
  noMinWidth?: boolean;
  useStaticMinute?: boolean; // ใช้ :00 เสมอ
  // callback
  onFormatBeforeChange?: (value: string) => string;
  onTextChange?: (time: string) => any;
};

const IconStyle = { top: "0.7rem" };

const TimeComboBox = React.forwardRef<any, TimeComboBoxProps>((props, ref) => {
  const {
    id,
    defaultValue,
    disabled,
    disableReorder = false,
    fluid: isFluid,
    icon = "time",
    inputStyle,
    interval = 30,
    maxHour = 30,
    minHour = 0,
    noMinWidth,
    placeholder = "Choose Time",
    style,
    useStaticMinute = false,
    onFormatBeforeChange,
    onTextChange,
  } = props;

  const [timeList, setTimeList] = useState<any[]>([]);
  const [time, setTime] = useState("");

  const inputRef = useRef<any>(null);

  useImperativeHandle<any, any>(ref, () => ({
    getValue: () => time,
    inputRef,
    setValue: (value: string) => {
      setTime(value);
      generateTimeOptions(value);
    },
  }));

  // Effect
  useEffect(() => {
    generateTimeOptions();
  }, []);

  useEffect(() => {
    let value = defaultValue || "";

    const reg = /^\d{2}:\d{2}:\d{2}$/;

    if (value.search(reg) >= 0) {
      value = value.replaceAll(/:\d{2}$/g, "");
    }

    setTime(value);
    generateTimeOptions(value);
  }, [defaultValue]);

  const handleChangeTime = (value: string) => {
    setTime(value);
    onTextChange?.(value);
    generateTimeOptions(value);
  };

  // Callback
  const handleChange = (e: any, v: any) => {
    if (v.value !== time) {
      handleChangeTime(v.value);
    }
  };

  // Memo
  const timeOptions = useMemo(
    () =>
      timeList.map((value, index) => ({
        key: index + 1,
        text: value,
        value,
      })),
    [timeList]
  );

  // แปลงทศนิยมเป็น HH:mm
  const formatTime = (decimalHour: number, defaultMin: string): string => {
    // ถ้าเป็นจำนวนเต็ม ใช้ min จาก parameter
    if (Number.isInteger(decimalHour)) {
      return `0${Math.floor(decimalHour)}`.slice(-2) + defaultMin;
    }
    // ถ้าเป็นทศนิยม แปลงเป็น HH:mm
    const hour = Math.floor(decimalHour);
    const minute = Math.round((decimalHour - hour) * 60);

    const paddedHour = `0${hour}`.slice(-2);
    const paddedMinute = `0${minute}`.slice(-2);

    return `${paddedHour}:${paddedMinute}`;
  };

  // Handler
  const generateTimeOptions = (inputTime = "") => {
    let min = ":00";

    // ถ้าไม่ใช้ static minute ให้ดึงจาก inputTime
    if (!useStaticMinute && inputTime) {
      const [, inputMin] = inputTime.split(":");

      min = inputMin ? `:${inputMin}` : ":00";
    }

    const date = moment().format("MM/DD/YYYY");

    let generatedTimeList: string[] = [];

    let start = moment(`${date} ${formatTime(minHour, min)}`, "MM/DD/YYYY HH:mm");

    let end = moment(`${date} ${formatTime(maxHour, min)}`, "MM/DD/YYYY HH:mm");

    if (Math.floor(maxHour) === 23 && Number.isInteger(maxHour)) {
      end = moment(`${date} 23:59`, "MM/DD/YYYY HH:mm");
    } else if (Math.floor(maxHour) > 23) {
      const remainHours = maxHour - 24;

      end = moment(`${date} ${formatTime(remainHours, min)}`, "MM/DD/YYYY HH:mm");
      end.add(1, "days");
    }

    while (start <= end) {
      const formattedTime = moment(start).format("HH:mm");

      generatedTimeList.push(formattedTime);
      start = moment(start).add(interval, "minutes");
    }

    // เพิ่ม missing value
    if (inputTime && !generatedTimeList.includes(inputTime)) {
      if (disableReorder) {
        // ถ้า disableReorder = true: แทรกตามลำดับเวลา
        generatedTimeList.push(inputTime);
        generatedTimeList.sort((a, b) => a.localeCompare(b));
      } else {
        // ถ้า disableReorder = false: ใส่ข้างบนสุด
        generatedTimeList.unshift(inputTime);
      }
    }

    // เรียงลำดับใหม่ตาม input (ถ้า disableReorder = false)
    if (!disableReorder && inputTime) {
      const prev: string[] = [];
      const next: string[] = [];

      for (const value of generatedTimeList) {
        if (Intl.Collator().compare(inputTime, value) > 0) {
          next.push(value);
        } else {
          prev.push(value);
        }
      }

      generatedTimeList = [...prev, ...next];
    }

    setTimeList([...generatedTimeList] as any);
  };

  return (
    <DropdownOptions
      ref={inputRef}
      id={id}
      style={style}
      // config
      disabled={disabled}
      fluid={isFluid}
      icon={icon}
      iconStyle={IconStyle}
      inputStyle={inputStyle}
      noMinWidth={noMinWidth}
      placeholder={placeholder}
      value={time}
      formatTimeOnBlur
      search
      options={timeOptions}
      // callback
      onChange={handleChange}
      onFormatBeforeChange={onFormatBeforeChange}
    />
  );
});

TimeComboBox.displayName = "TimeComboBox";

export default React.memo(TimeComboBox);
